import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import store from './store';
import AppRoutes from './routes';
import mixpanel from 'mixpanel-browser';
import './App.scss';
import { AD_CLICK_URL_ONLY_KEYS, persistAttribution, sanitizeCurrentUrlQuery } from './utils/attribution';
import { trackLandingPageView } from './utils/tracking';
import { capturePracticeEmailLanding } from './utils/growth-receipt';
import { initMobileAcquisitionAttribution } from './services/mobileAcquisitionAttribution';
import { initMobileRetentionTracking } from './services/mobileRetentionAnalytics';
import { isNativeCapacitorRuntime } from './services/revenueCatService';
import { installDevAuthStub } from './services/devAuthStubService';
import { mixpanelInitOptions } from './utils/mixpanel-options';

const App = () => {

    useEffect(() => {
        const attribution = persistAttribution();
        // A landing from the practice email waits for the sign-in (utils/growth-receipt).
        capturePracticeEmailLanding();
        sanitizeCurrentUrlQuery({
            allowedPathnames: ['/login', '/absite-program-code-request'],
            safeExtraParamKeys: ['next'],
            // Ad click ids stay in the address for the conversion linker
            // and Reddit's pixel (rdt_cid), and only there
            // (persistAttribution above never stores them).
            urlOnlyParamKeys: AD_CLICK_URL_ONLY_KEYS,
        });
        trackLandingPageView(attribution);
        // Native only: capture App Store deep-link / Universal-Link attribution
        // into the same first-party store. No-ops on web + never throws, so a
        // fire-and-forget call here is safe.
        initMobileAcquisitionAttribution();
        // Native only: emit foreground/background retention events (mobile
        // re-engagement is invisible to page-load web analytics in a WebView).
        initMobileRetentionTracking();
        // Native only: scope the iOS design-polish CSS (popups, safe-area,
        // premium paywall styling) under one body class so web CSS is
        // untouched by the mobile design system.
        if (isNativeCapacitorRuntime()) {
            document.body.classList.add('native-app');
        }
        // Dev capture builds only (REACT_APP_QLEARN_DEV_AUTHSTUB): auto-signin
        // + OTP intercept so the simulator lane can reach authed screens.
        // Compiles to a no-op in prod builds — see src/constants/devAuthStub.js.
        installDevAuthStub();
    }, []);


    // Mixpanel (opted out in the iOS build; see utils/mixpanel-options.js)
    mixpanel.init(process.env.REACT_APP_MIXPANEL_KEY, mixpanelInitOptions());
    // Hotjar is NOT initialized here any more. It recorded every route in
    // the product, including exam content and the student's own written
    // reasoning. <SessionReplayGate> inside <Router> starts it only on
    // allow-listed routes — see src/utils/session-replay.js.

    return (
        <Provider store={store}>
            <AppRoutes />
        </Provider>
    );
}

export default App;
